import { useState } from 'react'
import { AlertCircle, Trophy, Search, RefreshCw, Flame, ChevronDown, ChevronRight } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Label,
} from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { useWaiterExcellenceReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import type {
  WaiterExcellenceFilters,
  WaiterExcellenceRecord,
  WaiterUnmatched,
} from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

const tenge = (n: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n) + ' ₸'

// Первый день текущего месяца и сегодня — дефолтный период
function defaultPeriod(): { from: string; to: string } {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  const iso = (d: Date) => d.toISOString().split('T')[0]
  return { from: iso(from), to: iso(now) }
}

export function WaiterExcellenceReport() {
  const [filters, setFilters] = useState<WaiterExcellenceFilters>(() => ({
    ...defaultPeriod(),
    topN: 10,
    minChecks: 30,
  }))
  const [shouldFetch, setShouldFetch] = useState(false)
  const [showUnmatched, setShowUnmatched] = useState(false)

  const { data: organizations } = useOrganizationsForSelect() as {
    data: Organization[] | undefined
  }

  const { data: report, isLoading, error, isFetching, refetch } =
    useWaiterExcellenceReport(filters, shouldFetch)

  const handleGenerateReport = () => {
    setShouldFetch(true)
    if (shouldFetch) refetch()
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="we-org">Организация</Label>
              <select
                id="we-org"
                value={filters.organizationId || ''}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, organizationId: e.target.value || undefined }))
                }
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Все организации</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="we-from">С даты</Label>
              <input
                id="we-from"
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                className="h-10 w-[170px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="we-to">По дату</Label>
              <input
                id="we-to"
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                className="h-10 w-[170px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="we-topn">Топ-N</Label>
              <input
                id="we-topn"
                type="number"
                min={1}
                max={200}
                value={filters.topN ?? 10}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, topN: Number(e.target.value) || 10 }))
                }
                className="h-10 w-[90px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="we-minchecks">Мин. чеков</Label>
              <input
                id="we-minchecks"
                type="number"
                min={0}
                value={filters.minChecks ?? 30}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, minChecks: Number(e.target.value) || 0 }))
                }
                className="h-10 w-[90px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleGenerateReport} disabled={isLoading || isFetching} className="gap-2">
                {isLoading || isFetching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Сформировать отчет
              </Button>
              <Button
                variant="outline"
                onClick={() => setFilters({ ...defaultPeriod(), topN: 10, minChecks: 30 })}
              >
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчета. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {shouldFetch && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Отличники-официанты
            </CardTitle>
            <CardDescription>
              {report ? (
                <>
                  Топ-{report.meta.topN} по композитному баллу · {report.meta.from} — {report.meta.to} · веса:
                  продажи {Math.round(report.meta.weights.sales * 100)}% / ср.чек{' '}
                  {Math.round(report.meta.weights.avgCheck * 100)}% / XP{' '}
                  {Math.round(report.meta.weights.xp * 100)}% · мин. чеков {report.meta.min_checks} ·
                  в рейтинге {report.meta.ranked} из {report.meta.matched} сопоставленных
                  (отсеяно порогом {report.meta.below_min_checks}) · всего {report.meta.total_waiters}{' '}
                  официантов, {report.meta.departments} точек
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : report && report.data.length > 0 ? (
              <WaiterTable records={report.data} />
            ) : (
              <div className="text-center py-8">
                <Trophy className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Нет данных
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ни один официант не сопоставлен с сотрудником TCO за выбранный период.
                  Проверьте настройку «Smart Schedule (прогноз продаж)» у организаций.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Unmatched diagnostics */}
      {shouldFetch && report && report.unmatched.length > 0 && (
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => setShowUnmatched((v) => !v)}>
            <CardTitle className="flex items-center gap-2 text-base">
              {showUnmatched ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Не сопоставлено: {report.meta.unmatched}
            </CardTitle>
            <CardDescription>
              Официанты aqniet без совпадения по ФИО с сотрудником TCO (кассы, банкеты,
              уволенные, расхождения написания)
            </CardDescription>
          </CardHeader>
          {showUnmatched && (
            <CardContent>
              <UnmatchedTable records={report.unmatched} />
            </CardContent>
          )}
        </Card>
      )}

      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Trophy className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Нажмите "Сформировать отчет"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Рейтинг официантов по композитному баллу из суммы продаж, среднего чека
                (aqniet) и XP геймификации.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function WaiterTable({ records }: { records: WaiterExcellenceRecord[] }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>Сотрудник</TableHead>
            <TableHead>Организация</TableHead>
            <TableHead>Должность</TableHead>
            <TableHead className="text-right">Продажи</TableHead>
            <TableHead className="text-right">Ср. чек</TableHead>
            <TableHead className="text-center">Чеки</TableHead>
            <TableHead className="text-center">Уровень</TableHead>
            <TableHead className="text-center">XP</TableHead>
            <TableHead className="text-center">Серия</TableHead>
            <TableHead className="text-center">Балл</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.employee_id}>
              <TableCell className="font-medium">{r.rank}</TableCell>
              <TableCell className="font-medium">
                {r.employee_name}
                {r.department_name && (
                  <div className="text-xs text-muted-foreground">{r.department_name}</div>
                )}
              </TableCell>
              <TableCell>{r.organization_name || '-'}</TableCell>
              <TableCell>{r.position_name || '-'}</TableCell>
              <TableCell className="text-right">{tenge(r.total_sales)}</TableCell>
              <TableCell className="text-right">{tenge(r.avg_check)}</TableCell>
              <TableCell className="text-center">{r.checks_count}</TableCell>
              <TableCell className="text-center">{r.level}</TableCell>
              <TableCell className="text-center">{r.total_xp}</TableCell>
              <TableCell className="text-center">
                {r.current_streak_days > 0 ? (
                  <span className="inline-flex items-center gap-1 text-orange-600">
                    <Flame className="h-4 w-4" />
                    {r.current_streak_days}
                  </span>
                ) : (
                  '0'
                )}
              </TableCell>
              <TableCell className="text-center font-semibold text-primary">
                <span
                  title={`продажи ${r.score_sales} · ср.чек ${r.score_avg_check} · XP ${r.score_xp}`}
                >
                  {r.score}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function UnmatchedTable({ records }: { records: WaiterUnmatched[] }) {
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Официант (aqniet)</TableHead>
            <TableHead className="text-right">Продажи</TableHead>
            <TableHead className="text-right">Ср. чек</TableHead>
            <TableHead className="text-center">Чеки</TableHead>
            <TableHead className="text-center">Причина</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.aqniet_employee_id}>
              <TableCell>{r.waiter_names.join(' / ')}</TableCell>
              <TableCell className="text-right">{tenge(r.total_sales)}</TableCell>
              <TableCell className="text-right">{tenge(r.avg_check)}</TableCell>
              <TableCell className="text-center">{r.checks_count}</TableCell>
              <TableCell className="text-center">
                <span className="text-xs text-muted-foreground">
                  {r.reason === 'ambiguous' ? 'неоднозначно' : 'нет совпадения'}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
